import React, { useCallback, useEffect, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import platformService from '../api/platformService';

const MAX_UPLOAD_MB = 64;
const CONFIRM_WORD = 'RESTORE';

const STATUS_COLORS = {
  ready: 'bg-green-50 text-green-700',
  upload: 'bg-blue-50 text-blue-700',
  missing: 'bg-yellow-50 text-yellow-700',
};

// Error bodies of blob requests (downloads) arrive as a Blob, not JSON.
const extractError = async (err, fallback) => {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.messages?.error || parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }
  if (err.code === 'ECONNABORTED') return 'The request timed out. The operation may still be running on the server — reload in a minute.';
  return data?.messages?.error || data?.message || fallback;
};

// Danger confirmation shared by "Restore" and "Upload & Restore" — the
// owner must type RESTORE before the confirm button is enabled.
const RestoreConfirmModal = ({ title, description, busy, onCancel, onConfirm, children, canConfirm = true }) => {
  const [typed, setTyped] = useState('');
  const confirmed = typed.trim() === CONFIRM_WORD && canConfirm;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-start mb-4">
          <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-sm">
            This replaces the <strong>entire database</strong> — every tenant&apos;s data and the platform&apos;s own
            data (tenants, plans, contracts, console accounts). Anything created after the backup was taken will be
            lost. This cannot be undone. Run a fresh backup first if you may need the current state.
          </p>
        </div>

        {children}

        <label className="block text-sm font-medium text-gray-700 mb-1 mt-4">
          Type <span className="font-mono font-bold">{CONFIRM_WORD}</span> to confirm
        </label>
        <input
          className="input-field"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={busy}
          autoComplete="off"
        />

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onConfirm}
            disabled={!confirmed || busy}
          >
            {busy ? 'Restoring... (this can take several minutes)' : 'Restore Database'}
          </button>
        </div>
      </div>
    </div>
  );
};

const BackupsPage = () => {
  const [backups, setBackups] = useState([]);
  const [settings, setSettings] = useState(null);
  const [retentionInput, setRetentionInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [busy, setBusy] = useState(''); // '' | 'run' | 'settings' | 'restore' | `download:${id}` | `delete:${id}`
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    setIsLoading(true);
    platformService.getBackups()
      .then((res) => {
        if (res.status === 'success') {
          setBackups(res.data.backups || []);
          setSettings(res.data.settings || null);
          setRetentionInput(String(res.data.settings?.retention_days ?? ''));
        }
      })
      .catch(async (err) => setError(await extractError(err, 'Failed to load backups.')))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const startAction = (key) => {
    setBusy(key);
    setActionError('');
    setSuccessMessage('');
  };

  const handleRun = async () => {
    startAction('run');
    try {
      const res = await platformService.runBackup();
      setSuccessMessage(`${res.message || 'Backup completed.'} ${res.data?.filename ? `(${res.data.filename})` : ''}`);
      load();
    } catch (err) {
      setActionError(await extractError(err, 'Backup failed.'));
    } finally {
      setBusy('');
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    const days = parseInt(retentionInput, 10);
    if (!Number.isInteger(days) || days < 1 || days > 20) {
      setActionError('Retention must be a whole number between 1 and 20 days.');
      return;
    }
    startAction('settings');
    try {
      const res = await platformService.updateBackupSettings({ retention_days: days });
      setSettings(res.data);
      setSuccessMessage(res.message || 'Backup settings updated.');
    } catch (err) {
      setActionError(await extractError(err, 'Failed to update backup settings.'));
    } finally {
      setBusy('');
    }
  };

  const handleDownload = async (backup) => {
    startAction(`download:${backup._id}`);
    try {
      const blob = await platformService.downloadBackup(backup._id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = backup.filename || 'backup.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(await extractError(err, 'Download failed.'));
    } finally {
      setBusy('');
    }
  };

  const handleDelete = async () => {
    const backup = deleteTarget;
    startAction(`delete:${backup._id}`);
    try {
      await platformService.deleteBackup(backup._id);
      setDeleteTarget(null);
      setSuccessMessage(`Backup ${backup.filename} deleted.`);
      load();
    } catch (err) {
      setActionError(await extractError(err, 'Failed to delete backup.'));
    } finally {
      setBusy('');
    }
  };

  const handleRestore = async () => {
    startAction('restore');
    try {
      const res = await platformService.restoreBackup(restoreTarget._id);
      setRestoreTarget(null);
      setSuccessMessage(
        `Database restored from ${restoreTarget.filename}: ${res.data?.total_documents ?? 0} documents in ${res.data?.restored_collections ?? 0} collections.`
      );
      load();
    } catch (err) {
      setActionError(await extractError(err, 'Restore failed.'));
      setRestoreTarget(null);
    } finally {
      setBusy('');
    }
  };

  const uploadTooLarge = uploadFile && uploadFile.size > MAX_UPLOAD_MB * 1024 * 1024;
  const uploadNotZip = uploadFile && !uploadFile.name.toLowerCase().endsWith('.zip');

  const handleUploadRestore = async () => {
    startAction('restore');
    try {
      const res = await platformService.uploadAndRestoreBackup(uploadFile);
      setUploadOpen(false);
      setUploadFile(null);
      setSuccessMessage(res.message || 'Database restored from upload.');
      load();
    } catch (err) {
      setActionError(await extractError(err, 'Upload and restore failed.'));
      setUploadOpen(false);
      setUploadFile(null);
    } finally {
      setBusy('');
    }
  };

  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Backups</h1>
          <p className="text-sm text-gray-500 mt-1">Full-database backups (all tenants + platform). Owner only.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button className="btn-secondary" onClick={() => { setActionError(''); setUploadOpen(true); }} disabled={!!busy}>
            Upload &amp; Restore
          </button>
          <button className="btn-primary" onClick={handleRun} disabled={!!busy}>
            {busy === 'run' ? 'Running Backup...' : 'Run Backup Now'}
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
          <p className="text-green-700 text-sm">{successMessage}</p>
        </div>
      )}

      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-600 text-sm">{actionError}</p>
        </div>
      )}

      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Settings</h2>
        <p className="text-sm text-gray-500 mb-4">
          A full backup runs automatically every day at 02:00 WIB. Backups older than the retention period
          are deleted when a new backup runs.
          {settings?.last_run && <> Last run: <span className="font-medium text-gray-700">{settings.last_run}</span>.</>}
        </p>
        <form onSubmit={handleSaveSettings} className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Retention (days, 1–20)</label>
            <input
              type="number"
              min="1"
              max="20"
              className="input-field sm:!w-40"
              value={retentionInput}
              onChange={(e) => setRetentionInput(e.target.value)}
              disabled={isLoading || !!busy}
            />
          </div>
          <button type="submit" className="btn-primary" disabled={isLoading || !!busy}>
            {busy === 'settings' ? 'Saving...' : 'Save'}
          </button>
        </form>
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading...</p>
        ) : backups.length === 0 ? (
          <p className="p-6 text-gray-500">No backups yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="px-6 py-3">File</th>
                <th className="px-6 py-3">Created</th>
                <th className="px-6 py-3">Size</th>
                <th className="px-6 py-3">Documents</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => {
                const available = b.file_exists && ['ready', 'upload'].includes(b.status);
                return (
                  <tr key={b._id} className="border-b border-gray-100 last:border-0">
                    <td className="px-6 py-3 font-medium text-gray-900 whitespace-nowrap">
                      {b.filename}
                      {b.original_name && <div className="text-xs text-gray-500 font-normal">Uploaded: {b.original_name}</div>}
                    </td>
                    <td className="px-6 py-3 text-gray-600 whitespace-nowrap">{b.created_at || '—'}</td>
                    <td className="px-6 py-3 text-gray-600 whitespace-nowrap">{b.size_formatted || '—'}</td>
                    <td className="px-6 py-3 text-gray-600">
                      {Number(b.total_documents || 0).toLocaleString('id-ID')}
                      <span className="text-xs text-gray-400"> / {b.total_collections || 0} collections</span>
                    </td>
                    <td className="px-6 py-3">
                      <span className={`badge ${STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-700'}`}>{b.status}</span>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex gap-2">
                        <button
                          className="btn-secondary !px-3 !py-1.5 text-sm whitespace-nowrap"
                          onClick={() => handleDownload(b)}
                          disabled={!available || !!busy}
                        >
                          {busy === `download:${b._id}` ? 'Downloading...' : 'Download'}
                        </button>
                        <button
                          className="btn-secondary !px-3 !py-1.5 text-sm whitespace-nowrap text-red-700"
                          onClick={() => { setActionError(''); setRestoreTarget(b); }}
                          disabled={!available || !!busy}
                        >
                          Restore
                        </button>
                        <button
                          className="btn-secondary !px-3 !py-1.5 text-sm whitespace-nowrap"
                          onClick={() => { setActionError(''); setDeleteTarget(b); }}
                          disabled={!!busy}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {restoreTarget && (
        <RestoreConfirmModal
          title="Restore Database"
          description={`From backup ${restoreTarget.filename} (${restoreTarget.created_at || 'unknown date'}).`}
          busy={busy === 'restore'}
          onCancel={() => setRestoreTarget(null)}
          onConfirm={handleRestore}
        />
      )}

      {uploadOpen && (
        <RestoreConfirmModal
          title="Upload & Restore"
          description="Upload a backup ZIP created by this system and restore the database from it."
          busy={busy === 'restore'}
          canConfirm={!!uploadFile && !uploadTooLarge && !uploadNotZip}
          onCancel={() => { setUploadOpen(false); setUploadFile(null); }}
          onConfirm={handleUploadRestore}
        >
          <label className="block text-sm font-medium text-gray-700 mb-1">Backup file (.zip, max {MAX_UPLOAD_MB} MB)</label>
          <input
            type="file"
            accept=".zip,application/zip"
            className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-200 file:text-gray-900 hover:file:bg-gray-300"
            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            disabled={busy === 'restore'}
          />
          {uploadNotZip && <p className="error-message">Only .zip files are accepted.</p>}
          {uploadTooLarge && <p className="error-message">The file is larger than {MAX_UPLOAD_MB} MB.</p>}
        </RestoreConfirmModal>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Delete Backup</h2>
            <p className="text-sm text-gray-600">
              Delete <span className="font-medium">{deleteTarget.filename}</span>? The file is removed from the server.
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4">
              <button className="btn-secondary" onClick={() => setDeleteTarget(null)} disabled={!!busy}>Cancel</button>
              <button
                className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
                onClick={handleDelete}
                disabled={!!busy}
              >
                {busy.startsWith('delete:') ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupsPage;
